import React from "react";
import { Link } from "react-router-dom";
import { SESSION, IMAGES } from "../mock";
import { Reveal, SectionHeading, PageHero, Ornament } from "../components/bits";
import { Droplets, Clock, Sparkles } from "lucide-react";

const Session = () => {
  const { hero, gallery, intro, quote, intro2, pathways, science, phases } = SESSION;

  return (
    <main>
      <PageHero
        image="/session_meditation.webp"
        title={hero.title}
        sub={hero.sub}
        objectPosition="center 42%"
        overlay="linear-gradient(180deg, rgba(28,20,16,0.5), rgba(28,20,16,0.34)), linear-gradient(90deg, rgba(24,17,14,0.5) 0%, rgba(24,17,14,0.12) 55%, rgba(24,17,14,0) 100%)"
      />

      {/* Intro + cosmic gallery */}
      <section className="section bloom">
        <div className="container-page">
          <Reveal className="container-narrow">
            <div className="prose-body">
              {intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={80} className="cosmic-grid" style={{ margin: "3rem 0" }}>
            {gallery.map((g, i) => (
              <div key={i} className="img-frame">
                <img src={g.src} alt={g.alt} loading="lazy" />
              </div>
            ))}
          </Reveal>

          <Reveal className="container-narrow" style={{ textAlign: "center", margin: "2.5rem auto" }}>
            <p className="pull-quote">{quote}</p>
          </Reveal>

          <Reveal className="container-narrow">
            <div className="prose-body">
              {intro2.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUNDAY COMMON SESSION — free weekly shared session */}
      <section className="section bloom">
        <div className="container-page">
          <Reveal className="container-narrow" style={{ textAlign: "center" }}>
            <span className="eyebrow" style={{ color: "var(--sky)" }}>
              Every Sunday · 09:00 AM (CEST) · Free
            </span>
            <h2 className="h-section" style={{ marginTop: "0.6rem" }}>The Sunday Common Session</h2>
            <div className="prose-body" style={{ marginTop: "1.25rem", textAlign: "left" }}>
              <p>Once a week, we gather in the field of shared presence. The Sunday Common Session is an open invitation to experience NLFH without commitment or payment.</p>
              <p>There is nothing you need to do. You may simply rest quietly, sit or lie down, or continue with your ordinary activities.</p>
            </div>
            <p className="h-sub" style={{ marginTop: "1rem" }}>
              Approximately 20 minutes · Participation is free. No experience with meditation or NLFH is required.
            </p>
            <Link to="/contacts" className="btn-gold" style={{ marginTop: "1.5rem" }}>
              Join the Sunday Session
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Pathways */}
      <section className="section" style={{ background: "var(--paper-warm)" }}>
        <div className="container-page">
          <Reveal>
            <SectionHeading eyebrow="Three Pathways of Companionship" title={pathways.title} align="center" />
          </Reveal>
          <Reveal delay={90}>
            <p className="lead container-narrow" style={{ textAlign: "center", marginTop: "1.25rem" }}>
              {pathways.intro}
            </p>
          </Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
              marginTop: "3rem",
            }}
          >
            {pathways.options.map((o, i) => (
              <Reveal
                key={o.name}
                delay={i * 110}
                className="soft-card"
                style={{ padding: "2rem", display: "flex", flexDirection: "column" }}
              >
                <div className="eyebrow" style={{ color: "var(--sky)" }}>{o.tag}</div>
                <h3 style={{ fontSize: "1.55rem", margin: "0.6rem 0 0.15rem" }}>{o.name}</h3>
                <div className="h-sub" style={{ fontSize: "1rem", marginBottom: "1rem" }}>{o.subtitle}</div>
                <p style={{ color: "var(--ink-soft)", fontSize: "0.96rem", lineHeight: 1.75 }}>{o.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Science of common sessions */}
      <section className="section bloom">
        <div className="container-page">
          <Reveal>
            <SectionHeading eyebrow="Shared, Not Divided" title={science.title} sub={science.sub} align="center" />
          </Reveal>
          <Reveal delay={80}>
            <p className="lead container-narrow" style={{ textAlign: "center", marginTop: "1.25rem" }}>
              {science.body[0]}
            </p>
          </Reveal>
          <div className="feature-grid" style={{ marginTop: "3rem", alignItems: "stretch" }}>
            {science.metaphors.map((m, i) => (
              <Reveal key={m.name} delay={i * 120} className="soft-card" style={{ padding: "2.25rem" }}>
                <div className="ornament" style={{ marginBottom: "1rem" }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M6 0 L8 4 L12 6 L8 8 L6 12 L4 8 L0 6 L4 4 Z" fill="currentColor" />
                  </svg>
                </div>
                <h3 style={{ fontSize: "1.55rem", marginBottom: "0.85rem" }}>{m.name}</h3>
                <p style={{ color: "var(--ink-soft)", lineHeight: 1.8 }}>{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Field in practice - quick facts */}
      <section className="section-tight" style={{ background: "var(--paper-warm)" }}>
        <div className="container-page">
          <div className="pillars-grid">
            {[
              { icon: Clock, title: "~60 Minutes", body: "Each session includes your Companion's silent internal preparation before your attunement begins." },
              { icon: Sparkles, title: "The Art of Non-Doing", body: "You are not required to meditate or hold focus. Your system registers the field independently of the analytical mind." },
              { icon: Droplets, title: "Gentle Preparation", body: "Two glasses of water beforehand support cellular conductivity; rest quietly if you wish to perceive subtle shifts." },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={i * 100} className="pillar">
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      margin: "0 auto 1rem",
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: "#fff",
                      border: "1px solid var(--line)",
                    }}
                  >
                    <Icon size={26} color="var(--sky)" strokeWidth={1.5} />
                  </div>
                  <h3 style={{ fontSize: "1.3rem" }}>{f.title}</h3>
                  <p style={{ color: "var(--ink-soft)", fontSize: "0.95rem", marginTop: "0.5rem" }}>{f.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Seven phases */}
      <section className="section bloom">
        <div className="container-page">
          <Reveal>
            <SectionHeading eyebrow="While You Rest" title={phases.title} sub={phases.sub} align="center" />
          </Reveal>
          <div className="container-narrow" style={{ marginTop: "3rem" }}>
            {phases.items.map((p, i) => (
              <Reveal
                key={p.name}
                delay={i * 60}
                style={{
                  display: "flex",
                  gap: "1.5rem",
                  padding: "1.4rem 0",
                  borderBottom: i < phases.items.length - 1 ? "1px solid var(--line)" : "none",
                }}
              >
                <div
                  style={{
                    flex: "none",
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background: "linear-gradient(160deg, #fbf3da, #f3e7bd)",
                    border: "1px solid var(--gold-strong)",
                    fontFamily: "var(--font-serif)",
                    fontSize: "1.2rem",
                    color: "var(--gold-ink)",
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <h3 style={{ fontSize: "1.3rem", marginBottom: "0.3rem" }}>{p.name}</h3>
                  <p style={{ color: "var(--ink-soft)", fontSize: "0.98rem" }}>{p.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="container-narrow" style={{ textAlign: "center", marginTop: "3.5rem" }}>
            <Ornament style={{ marginBottom: "1.25rem", justifyContent: "center", display: "inline-flex" }} />
            <Link to="/contacts" className="btn-gold">
              Coordinate Your Session
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Session;
